/**
 * @file useDigest Hook
 * @description React Query hooks for daily digest report operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  digestService,
  GenerateDigestDto,
  GenerateDigestResponse,
  DigestHistoryItem,
  DigestRecord,
} from '../services/digest.service';
import { unwrapResponse } from '../types';

/**
 * Query keys for digest operations
 */
export const digestKeys = {
  all: ['digest'] as const,
  history: () => [...digestKeys.all, 'history'] as const,
  details: () => [...digestKeys.all, 'detail'] as const,
  detail: (id: number) => [...digestKeys.details(), id] as const,
  latest: () => [...digestKeys.all, 'latest'] as const,
};

/**
 * Fetch digest history
 */
export function useDigestHistory(
  limit?: number,
  options?: Omit<UseQueryOptions<DigestHistoryItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DigestHistoryItem[], Error>({
    queryKey: [...digestKeys.history(), limit],
    queryFn: async () => {
      const response = await digestService.getHistory(limit);
      return unwrapResponse<DigestHistoryItem[]>(response.data);
    },
    ...options,
  });
}

/**
 * Fetch a specific digest by ID
 */
export function useDigest(
  id: number,
  options?: Omit<UseQueryOptions<DigestRecord, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DigestRecord, Error>({
    queryKey: digestKeys.detail(id),
    queryFn: async () => {
      const response = await digestService.getById(id);
      return unwrapResponse<DigestRecord>(response.data);
    },
    enabled: id > 0,
    ...options,
  });
}

/**
 * Fetch the latest digest
 */
export function useLatestDigest(
  options?: Omit<UseQueryOptions<DigestRecord | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DigestRecord | null, Error>({
    queryKey: digestKeys.latest(),
    queryFn: async () => {
      const response = await digestService.getLatest();
      return unwrapResponse<DigestRecord | null>(response.data);
    },
    ...options,
  });
}

/**
 * Generate a new digest report
 */
export function useGenerateDigest(
  options?: UseMutationOptions<GenerateDigestResponse, Error, GenerateDigestDto | undefined>
) {
  const queryClient = useQueryClient();

  return useMutation<GenerateDigestResponse, Error, GenerateDigestDto | undefined>({
    mutationFn: async (data) => {
      const response = await digestService.generate(data);
      return unwrapResponse<GenerateDigestResponse>(response.data);
    },
    onSuccess: () => {
      // Invalidate history and latest queries after generating a new digest
      queryClient.invalidateQueries({ queryKey: digestKeys.history() });
      queryClient.invalidateQueries({ queryKey: digestKeys.latest() });
    },
    ...options,
  });
}
